import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { InfoPagesLayout } from "../../components/layout/info-pages-layout";
import { FormattedMessage } from "react-intl";
import { 
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountCircle as AccountIcon,
  Gavel as GavelIcon,
  Payment as PaymentIcon,
  Star as StarIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Shield as ShieldIcon,
  Copyright as CopyrightIcon,
  Code as CodeIcon,
  Gavel as GavelIcon2,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Menu as MenuIcon
} from "@mui/icons-material";

export const AGBPage = () => {
  const themeContext = useTheme();
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = [
    { id: 'scope', title: 'Geltungsbereich', icon: <BusinessIcon /> },
    { id: 'parties', title: 'Vertragspartner', icon: <PersonIcon /> },
    { id: 'registration', title: 'Registrierung', icon: <AccountIcon /> },
    { id: 'obligations', title: 'Nutzerpflichten', icon: <GavelIcon /> },
    { id: 'services', title: 'Leistungen', icon: <BusinessIcon /> },
    { id: 'payment', title: 'Bezahlung', icon: <PaymentIcon /> },
    { id: 'reviews', title: 'Bewertungen', icon: <StarIcon /> },
    { id: 'privacy', title: 'Datenschutz', icon: <SecurityIcon /> },
    { id: 'liability', title: 'Haftungsausschluss', icon: <WarningIcon /> },
    { id: 'account', title: 'Benutzerkonto', icon: <ShieldIcon /> },
    { id: 'copyright', title: 'Urheberrechte', icon: <CopyrightIcon /> },
    { id: 'conduct', title: 'Verhaltenskodex', icon: <CodeIcon /> },
    { id: 'conflicts', title: 'Konfliktlösung', icon: <GavelIcon2 /> },
    { id: 'changes', title: 'AGB-Änderungen', icon: <SettingsIcon /> },
    { id: 'final', title: 'Schlussbestimmungen', icon: <InfoIcon /> }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = window.innerWidth <= 768 ? 120 : 220;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <InfoPagesLayout title={<FormattedMessage id={"footer.general-terms"} /> }>
        <PageContainer>
          <TableOfContents>
            <TocTitle>
              <MenuIcon />
              Inhaltsverzeichnis
            </TocTitle>
            <TocList>
              {sections.map((section) => (
                <TocItem 
                  key={section.id}
                  active={activeSection === section.id}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.icon}
                  {section.title}
                </TocItem>
              ))}
            </TocList>
          </TableOfContents>

          <ContentWrapper>
            <Section id="scope">
              <SectionHeader>
                <BusinessIcon />
                <h2>1. Geltungsbereich</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Die vorliegenden Allgemeinen Geschäftsbedingungen (nachfolgend "AGB")
                  gelten für sämtliche Leistungen und Angebote der Internetplattform
                  "Blagoom.de" (nachfolgend "Blagoom" oder "wir" genannt).
                </p>
                <p>
                  Durch die Registrierung auf Blagoom.de stimmen Nutzer diesen AGB zu. 
                  Sollten diese AGB in verschiedenen Sprachen zur Verfügung gestellt werden, 
                  ist im Zweifel die deutsche Fassung maßgeblich.
                </p>
              </ContentCard>
            </Section>

            <Section id="parties">
              <SectionHeader>
                <PersonIcon />
                <h2>2. Vertragspartner</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Vertragspartner sind die Nutzer der Plattform, also Nachhilfegeber und
                  Nachhilfenehmer, die jeweils ein rechtsverbindliches Angebot abgeben
                  oder annehmen.
                </p>
              </ContentCard>
            </Section>

            <Section id="registration">
              <SectionHeader>
                <AccountIcon />
                <h2>3. Registrierung und Nutzerkonto</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Die Anmeldung und die Erstellung eines Profils auf Blagoom.de ist
                  kostenlos. Jeder Nutzer darf nur ein Konto eröffnen.
                </p>
                
                <RegistrationInfo>
                  <h4>Registrierungsanforderungen:</h4>
                  <ul>
                    <li>Wahre und vollständige Angaben bei der Anmeldung</li>
                    <li>Nur ein Konto pro Nutzer erlaubt</li>
                    <li>Kostenlose Nutzung der Plattform</li>
                    <li>Jederzeitige Konto-Löschung möglich</li>
                  </ul>
                </RegistrationInfo>

                <p>
                  Durch die Nutzung der Website und/oder die Erstellung eines Benutzerkontos
                  stimmt der Nutzer den hier aufgeführten AGB zu. Nutzer können ihr Konto
                  jederzeit ohne Angabe von Gründen löschen.
                </p>
                <p>
                  <strong>Wichtiger Hinweis:</strong> Mit dem Löschen eines Kontos
                  werden alle auf Blagoom.de gespeicherten Daten des Nutzers
                  unwiderruflich gelöscht oder anonymisiert.
                </p>
              </ContentCard>
            </Section>

            <Section id="obligations">
              <SectionHeader>
                <GavelIcon />
                <h2>4. Pflichten der Nutzer</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Die Nutzer sind verpflichtet, die Kommunikation auf der Plattform
                  respektvoll und gesetzestreu zu gestalten.
                </p>
                
                <ObligationsList>
                  <h4>Verbotene Aktivitäten:</h4>
                  <ul>
                    <li>Beleidigende oder diskriminierende Äußerungen</li>
                    <li>Illegale Aktivitäten oder Inhalte</li>
                    <li>Verletzung der Rechte Dritter</li>
                    <li>Kommerzielle Nutzung ohne Genehmigung</li>
                  </ul>
                </ObligationsList>

                <WarningBox>
                  <strong>Konsequenzen:</strong> Verstöße gegen diese Pflichten führen 
                  zum sofortigen Ausschluss von der Plattform.
                </WarningBox>
              </ContentCard>
            </Section>

            <Section id="services">
              <SectionHeader>
                <BusinessIcon />
                <h2>5. Leistung von Blagoom</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Blagoom stellt lediglich die Plattform zur Verfügung, auf der Nachhilfe
                  angeboten und nachgefragt werden kann.
                </p>
                
                <ServiceLimitations>
                  <h4>Wichtige Hinweise zu unseren Leistungen:</h4>
                  <ul>
                    <li>Wir prüfen nicht die Fähigkeiten der Nachhilfegeber</li>
                    <li>Keine Garantie für Korrektheit der Universitätsmodule</li>
                    <li>Bewertungen werden nach bestimmter Methode zusammengefasst</li>
                    <li>Recht zur Änderung des Dienstumfangs vorbehalten</li>
                  </ul>
                </ServiceLimitations>

                <p>
                  Blagoom behält sich das Recht vor, den Umfang und die Art der auf der 
                  Plattform angebotenen Dienste jederzeit zu ändern, zu erweitern, oder einzuschränken.
                </p>
              </ContentCard>
            </Section>

            <Section id="payment">
              <SectionHeader>
                <PaymentIcon />
                <h2>6. Bezahlung</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Die Bezahlung für die Nachhilfe erfolgt ausschließlich zwischen
                  Nachhilfegeber und Nachhilfenehmer.
                </p>
                
                <PaymentInfo>
                  <h4>Zahlungsmodalitäten:</h4>
                  <ul>
                    <li>Direkte Abwicklung zwischen den Parteien</li>
                    <li>Blagoom ist nicht in Zahlungsprozesse involviert</li>
                    <li>Keine Haftung für Zahlungsprobleme</li>
                    <li>Keine Vermittlung bei Zahlungsstreitigkeiten</li>
                  </ul>
                </PaymentInfo>

                <p>
                  <strong>Wichtiger Hinweis:</strong> Blagoom ist nicht für die Bezahlung
                  verantwortlich und übernimmt auch keine Haftung für etwaige
                  Zahlungsprobleme.
                </p>
              </ContentCard>
            </Section>

            <Section id="reviews">
              <SectionHeader>
                <StarIcon />
                <h2>7. Bewertungen</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Bewertungen sollen wahrheitsgetreu und sachlich sein. Nutzer sind allein
                  für ihren Bewertungstext verantwortlich.
                </p>
                
                <ReviewGuidelines>
                  <h4>Bewertungsrichtlinien:</h4>
                  <ul>
                    <li>Wahrheitsgetreue und sachliche Bewertungen</li>
                    <li>Eigene Verantwortung für Bewertungstexte</li>
                    <li>Keine beleidigenden oder diskriminierenden Inhalte</li>
                    <li>Blagoom behält sich Löschung vor bei Verstößen</li>
                  </ul>
                </ReviewGuidelines>

                <p>
                  Blagoom behält sich vor, Bewertungen zu löschen, die gegen diese 
                  Vorschriften verstoßen.
                </p>
              </ContentCard>
            </Section>

            <Section id="privacy">
              <SectionHeader>
                <SecurityIcon />
                <h2>8. Datenschutz</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Die von Nutzern bereitgestellten Daten werden von Blagoom ausschließlich
                  zum Betreiben der Plattform genutzt und nicht an Dritte weitergeben oder
                  verkauft.
                </p>
                
                <PrivacyInfo>
                  <h4>Datenverwendung:</h4>
                  <ul>
                    <li>Daten werden ausschließlich für Plattformbetrieb genutzt</li>
                    <li>Keine Weitergabe an Dritte oder Verkauf</li>
                    <li>Informationen werden im Buchungsprozess weitergegeben</li>
                    <li>Telefonnummern werden an Unterrichtspartner weitergegeben</li>
                  </ul>
                </PrivacyInfo>

                <p>
                  Im Rahmen der Nutzung unserer Plattform werden einige Deiner
                  Informationen an andere Nutzer weitergegeben, entweder über Dein
                  öffentliches Profil oder im Rahmen des Buchungsprozesses.
                </p>
              </ContentCard>
            </Section>

            <Section id="liability">
              <SectionHeader>
                <WarningIcon />
                <h2>9. Haftungsausschluss</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Blagoom haftet nicht für die Qualität der von Nachhilfegebern erbrachten
                  Leistungen.
                </p>
                
                <LiabilityLimitations>
                  <h4>Haftungsausschlüsse:</h4>
                  <ul>
                    <li>Keine Haftung für Qualität der Nachhilfeleistungen</li>
                    <li>Keine Haftung für Schäden durch Nutzung/Nichtnutzung</li>
                    <li>Keine Gewährleistung für technische Fehler oder Ausfälle</li>
                    <li>Keine Haftung für Inhalte externer Links</li>
                    <li>Keine Haftung für Datenverlust oder entgangene Profite</li>
                  </ul>
                </LiabilityLimitations>

                <p>
                  Ebenso haftet Blagoom nicht für Schäden, die aus der Nutzung
                  oder Nichtnutzung der Plattform entstehen. Blagoom übernimmt keine
                  Gewährleistung oder Haftung für technische Fehler, Ausfälle oder
                  Sicherheitslücken.
                </p>
              </ContentCard>
            </Section>

            <Section id="account">
              <SectionHeader>
                <ShieldIcon />
                <h2>10. Benutzerkonto</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Nutzer sind für die Sicherheit ihres Benutzerkontos und ihre
                  Log-In-Daten verantwortlich.
                </p>
                
                <AccountSecurity>
                  <h4>Sicherheitsrichtlinien:</h4>
                  <ul>
                    <li>Eigene Verantwortung für Kontosicherheit</li>
                    <li>Sofortige Meldung bei Missbrauch oder Verlust</li>
                    <li>Blagoom kann verdächtige Konten sperren</li>
                    <li>Recht zur Kontolöschung bei Verstößen</li>
                  </ul>
                </AccountSecurity>

                <p>
                  Bei Missbrauch oder Verlust der Zugangsdaten ist Blagoom unverzüglich 
                  zu informieren. Blagoom behält sich das Recht vor, bei verdächtigen 
                  Aktivitäten Konten zu sperren oder zu löschen.
                </p>
              </ContentCard>
            </Section>

            <Section id="copyright">
              <SectionHeader>
                <CopyrightIcon />
                <h2>11. Urheber- und Nutzungsrechte</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Die Nutzer garantieren, dass sie über alle notwendigen Rechte an den von
                  ihnen bereitgestellten Inhalten verfügen.
                </p>
                
                <CopyrightInfo>
                  <h4>Rechte und Pflichten:</h4>
                  <ul>
                    <li>Garantie für alle notwendigen Rechte an Inhalten</li>
                    <li>Uneingeschränktes Nutzungsrecht für Blagoom</li>
                    <li>Kommerzielle Nutzung nur mit schriftlicher Genehmigung</li>
                    <li>Verbot von Werbung ohne Genehmigung</li>
                  </ul>
                </CopyrightInfo>

                <p>
                  Mit der Bereitstellung von Inhalten räumen die Nutzer Blagoom ein 
                  uneingeschränktes Nutzungsrecht ein. Die Nutzung der Plattform für 
                  kommerzielle Zwecke oder Werbung jeglicher Art ist ohne vorherige 
                  schriftliche Genehmigung durch Blagoom untersagt.
                </p>
              </ContentCard>
            </Section>

            <Section id="conduct">
              <SectionHeader>
                <CodeIcon />
                <h2>12. Verhaltenskodex</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Jeder Nutzer hat dafür Sorge zu tragen, dass er durch sein Verhalten
                  nicht die Rechte Dritter verletzt.
                </p>
                
                <ConductRules>
                  <h4>Verhaltensregeln:</h4>
                  <ul>
                    <li>Respektvoller Umgang mit anderen Nutzern</li>
                    <li>Keine Diskriminierung, Bedrohung oder Belästigung</li>
                    <li>Keine Beleidigungen oder beleidigende Inhalte</li>
                    <li>Einhaltung aller geltenden Gesetze</li>
                  </ul>
                </ConductRules>

                <WarningBox>
                  <strong>Konsequenzen:</strong> Blagoom behält sich das Recht vor, 
                  Nutzerprofile zu löschen, wenn diese gegen die AGB verstoßen, 
                  illegale Aktivitäten betreiben oder die Plattform schädigen.
                </WarningBox>
              </ContentCard>
            </Section>

            <Section id="conflicts">
              <SectionHeader>
                <GavelIcon2 />
                <h2>13. Konfliktlösung</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Im Streitfall zwischen den Nutzern übernimmt Blagoom keine Rolle als
                  Vermittler oder Schiedsrichter.
                </p>
                
                <ConflictResolution>
                  <h4>Streitbeilegung:</h4>
                  <ul>
                    <li>Blagoom vermittelt nicht bei Nutzerkonflikten</li>
                    <li>Es gilt die gesetzliche Rechtsprechung</li>
                    <li>Direkte Kommunikation zwischen den Parteien</li>
                    <li>Rechtliche Schritte bei Bedarf möglich</li>
                  </ul>
                </ConflictResolution>

                <p>
                  Es gilt die gesetzliche Rechtsprechung. Nutzer sollten Konflikte 
                  direkt miteinander lösen oder rechtliche Beratung in Anspruch nehmen.
                </p>
              </ContentCard>
            </Section>

            <Section id="changes">
              <SectionHeader>
                <SettingsIcon />
                <h2>14. Änderung der AGBs</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Blagoom behält sich vor, die AGBs jederzeit zu ändern. Über Änderungen
                  werden die Nutzer rechtzeitig informiert.
                </p>
                
                <ChangesInfo>
                  <h4>Änderungsrechte:</h4>
                  <ul>
                    <li>Jederzeitige Änderung der AGBs möglich</li>
                    <li>Rechtzeitige Information über Änderungen</li>
                    <li>Recht zur Service-Erweiterung oder -Einschränkung</li>
                    <li>Kein Anspruch auf Verfügbarkeit bestimmter Funktionen</li>
                  </ul>
                </ChangesInfo>

                <p>
                  Blagoom behält sich das Recht vor, den Service zu erweitern, zu ändern 
                  oder eingestellte Funktionen zu entfernen. Es besteht kein Anspruch 
                  auf die Verfügbarkeit bestimmter Funktionen.
                </p>
              </ContentCard>
            </Section>

            <Section id="final">
              <SectionHeader>
                <InfoIcon />
                <h2>15. Schlussbestimmungen</h2>
              </SectionHeader>
              <ContentCard>
                <p>
                  Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, 
                  bleibt die Wirksamkeit der übrigen Bestimmungen hiervon unberührt.
                </p>
                
                <FinalProvisions>
                  <h4>Rechtliche Bestimmungen:</h4>
                  <ul>
                    <li>Deutsches Recht unter Ausschluss des UN-Kaufrechts</li>
                    <li>Gerichtsstand ist der Sitz von Blagoom</li>
                    <li>Keine Abtretung von Ansprüchen ohne Zustimmung</li>
                    <li>Salvatorische Klausel bei unwirksamen Bestimmungen</li>
                  </ul>
                </FinalProvisions>

                <p>
                  Die unwirksame Bestimmung ist durch eine wirksame zu ersetzen, die 
                  dem verfolgten Zweck möglichst nahekommt. Es gilt das Recht der 
                  Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.
                </p>
              </ContentCard>
            </Section>
          </ContentWrapper>
        </PageContainer>
      </InfoPagesLayout>
    </>
  );
};

// Styled Components
const PageContainer = styled.div`
  display: flex;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  margin-top: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    margin: 0;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    gap: 0.5rem;
  }
`;

const TableOfContents = styled.div`
  position: sticky;
  top: 220px;
  width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  height: fit-content;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  flex-shrink: 0;

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    width: 100%;
    max-height: none;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 8px;
  }
`;

const TocTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  color: ${props => props.theme.mainTextColor};
  font-size: 1.1rem;
  font-weight: 600;
`;

const TocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  color: ${props => props.active ? props.theme.primaryColor : props.theme.mainTextColor};
  background: ${props => props.active ? props.theme.primary + '20' : 'transparent'};
  border-left: 3px solid ${props => props.active ? props.theme.primaryColor : 'transparent'};

  &:hover {
    background: ${props => props.theme.primary + '10'};
    transform: translateX(4px);
  }

  svg {
    font-size: 1.1rem;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: hidden;
  }
`;

const Section = styled.section`
  margin-bottom: 3rem;
  scroll-margin-top: 220px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    scroll-margin-top: 120px;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.primaryColor};

  h2 {
    margin: 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 2rem;
    font-weight: 600;
  }

  svg {
    font-size: 2rem;
    color: ${props => props.theme.primaryColor};
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  p {
    line-height: 1.7;
    margin-bottom: 1rem;
    color: ${props => props.theme.mainTextColor};
    word-wrap: break-word;
    overflow-wrap: break-word;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: ${props => props.theme.primaryColor};
    font-weight: 600;
  }
`;

const RegistrationInfo = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const ObligationsList = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const WarningBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  color: #856404;

  strong {
    color: #856404;
  }
`;

const ServiceLimitations = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const PaymentInfo = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const ReviewGuidelines = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const PrivacyInfo = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const LiabilityLimitations = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const AccountSecurity = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const CopyrightInfo = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const ConductRules = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const ConflictResolution = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const ChangesInfo = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const FinalProvisions = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;